import { Badge, Eyebrow, StrongText, Subheading, SurfaceCard, SurfaceRow } from '../../../components/ui'
import { NUTRITION_SUMMARY, TODAYS_MEALS, NUTRITION_WEEK } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import { GradientSphereMetricCard, sphereColorAt } from '../components/GradientSphereCard'

const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_MUTED = uiClasses.textMuted
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint
const BAR_TRACK = uiClasses.barTrack

const MAX_WEEKLY_CAL = Math.max(...NUTRITION_WEEK.map((d) => d.calories))

interface ProgressBarProps {
  color?: string
  max: number
  value: number
}

interface MacroRowProps {
  color: string
  label: string
  target: number
  unit?: string
  value: number
}

function ProgressBar({ value, max, color = 'dash-viz-tone-1' }: ProgressBarProps) {
  const pct = Math.min((value / max) * 100, 100)
  return (
    <div className={`h-2 w-full overflow-hidden rounded-full ${BAR_TRACK}`}>
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  )
}

function MacroRow({ label, value, target, unit = 'g', color }: MacroRowProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className={TEXT_MUTED}>{label}</span>
        <span className={`font-medium ${TEXT_PRIMARY}`}>
          {value}{unit} <span className={`font-normal ${TEXT_FAINT}`}>/ {target}{unit}</span>
        </span>
      </div>
      <ProgressBar value={value} max={target} color={color} />
    </div>
  )
}

export default function NutritionTab() {
  const s = NUTRITION_SUMMARY
  const calPct = Math.round((s.avgCalories / s.calorieTarget) * 100)
  const hydPct = Math.round((s.hydration / s.hydrationTarget) * 100)
  const hydrationRemaining = Math.max(s.hydrationTarget - s.hydration, 0)
  const totalCaloriesToday = TODAYS_MEALS.reduce((sum, meal) => sum + meal.calories, 0)
  const totalProteinToday = TODAYS_MEALS.reduce((sum, meal) => sum + meal.protein, 0)
  const totalCarbsToday = TODAYS_MEALS.reduce((sum, meal) => sum + meal.carbs, 0)
  const totalFatToday = TODAYS_MEALS.reduce((sum, meal) => sum + meal.fat, 0)
  const weeklyAverageCalories = Math.round(
    NUTRITION_WEEK.reduce((sum, day) => sum + day.calories, 0) / NUTRITION_WEEK.length
  )
  const highestCalorieDay = NUTRITION_WEEK.reduce((currentMax, day) =>
    day.calories > currentMax.calories ? day : currentMax
  )
  const lowestCalorieDay = NUTRITION_WEEK.reduce((currentMin, day) =>
    day.calories < currentMin.calories ? day : currentMin
  )

  return (
    <div className="space-y-6">
      <SurfaceCard as="article" className="nutrition-header-shell">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Nutrition command center</Subheading>
          <Badge variant="zinc">{calPct}% calorie target</Badge>
        </div>

        <div className="nutrition-summary-grid mt-5">
          <GradientSphereMetricCard label="Avg calories" value={s.avgCalories} sphereColor={sphereColorAt(0)} />
          <GradientSphereMetricCard
            label="Hydration today"
            sphereColor={sphereColorAt(1)}
            value={
              <>
                {s.hydration}
                <span className="gradient-sphere-metric-card__unit">L</span>
              </>
            }
          />
          <GradientSphereMetricCard
            label="Avg protein"
            sphereColor={sphereColorAt(2)}
            value={
              <>
                {s.avgProtein}
                <span className="gradient-sphere-metric-card__unit">g</span>
              </>
            }
          />
          <GradientSphereMetricCard label="Meals logged" value={TODAYS_MEALS.length} sphereColor={sphereColorAt(3)} />
        </div>

        <div className="nutrition-daily-layout mt-5">
          <SurfaceRow as="section" className="nutrition-focus-panel">
            <Eyebrow variant="section" tone="subtle">Energy and macros</Eyebrow>
            <div className="mt-4 flex items-center gap-6">
              <div className="relative flex size-28 shrink-0 items-center justify-center">
                <svg className="absolute inset-0 size-full -rotate-90" viewBox="0 0 36 36">
                  <circle
                    cx="18" cy="18" r="15.9155"
                    fill="none"
                    stroke="var(--nutrition-ring-track)"
                    strokeWidth="2.5"
                  />
                  <circle
                    cx="18" cy="18" r="15.9155"
                    fill="none"
                    stroke="var(--nutrition-ring-energy)"
                    strokeWidth="2.5"
                    strokeDasharray={`${calPct}, 100`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="text-center">
                  <p className={`text-xl font-bold ${TEXT_PRIMARY}`}>{s.avgCalories}</p>
                  <p className={`text-[10px] ${TEXT_SUBTLE}`}>/ {s.calorieTarget} kcal</p>
                </div>
              </div>

              <div className="flex-1 space-y-3">
                <MacroRow label="Protein" value={s.avgProtein} target={s.proteinTarget} color="dash-viz-tone-1" />
                <MacroRow label="Carbs" value={s.avgCarbs} target={s.carbTarget} color="dash-viz-tone-2" />
                <MacroRow label="Fat" value={s.avgFat} target={s.fatTarget} color="dash-viz-tone-3" />
              </div>
            </div>
          </SurfaceRow>

          <SurfaceRow as="section" className="nutrition-focus-panel">
            <Eyebrow variant="section" tone="subtle">Hydration</Eyebrow>
            <div className="mt-4 flex items-center gap-6">
              <div className="relative flex size-28 shrink-0 items-center justify-center">
                <svg className="absolute inset-0 size-full -rotate-90" viewBox="0 0 36 36">
                  <circle
                    cx="18" cy="18" r="15.9155"
                    fill="none"
                    stroke="var(--nutrition-ring-track)"
                    strokeWidth="2.5"
                  />
                  <circle
                    cx="18" cy="18" r="15.9155"
                    fill="none"
                    stroke="var(--nutrition-ring-hydration)"
                    strokeWidth="2.5"
                    strokeDasharray={`${hydPct}, 100`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="text-center">
                  <p className={`text-xl font-bold ${TEXT_PRIMARY}`}>{s.hydration}L</p>
                  <p className={`text-[10px] ${TEXT_SUBTLE}`}>/ {s.hydrationTarget}L goal</p>
                </div>
              </div>

              <div className="flex-1 space-y-3">
                <div>
                  <div className="flex items-center justify-between text-sm/6">
                    <span className={TEXT_MUTED}>Progress</span>
                    <span className={`font-semibold ${TEXT_PRIMARY}`}>{hydPct}%</span>
                  </div>
                  <div className="mt-2">
                    <ProgressBar value={s.hydration} max={s.hydrationTarget} color="dash-viz-tone-2" />
                  </div>
                </div>
                <p className={`text-xs/5 ${TEXT_SUBTLE}`}>
                  {hydrationRemaining > 0
                    ? `${hydrationRemaining.toFixed(1)}L remaining today`
                    : 'Daily hydration target reached'}
                </p>
              </div>
            </div>
          </SurfaceRow>
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Today's meals</Subheading>
          <Badge variant="zinc">{TODAYS_MEALS.length} logged</Badge>
        </div>

        <div className="nutrition-meal-grid mt-4">
          {TODAYS_MEALS.map((meal) => (
            <SurfaceRow key={meal.id}>
              <div className="flex items-start justify-between">
                <div>
                  <p className={`font-medium ${TEXT_PRIMARY}`}>{meal.name}</p>
                  <p className={`text-xs ${TEXT_SUBTLE}`}>{meal.time}</p>
                </div>
                <span className={`text-lg font-semibold ${TEXT_PRIMARY}`}>
                  {meal.calories}
                  <span className={`ml-0.5 text-xs font-normal ${TEXT_FAINT}`}>kcal</span>
                </span>
              </div>

              <p className={`mt-2 text-sm ${TEXT_SECONDARY}`}>{meal.items}</p>

              <div className="mt-3 flex gap-2">
                <Badge variant="zinc">P {meal.protein}g</Badge>
                <Badge variant="zinc">C {meal.carbs}g</Badge>
                <Badge variant="zinc">F {meal.fat}g</Badge>
              </div>
            </SurfaceRow>
          ))}
        </div>

        <div className="nutrition-meal-totals mt-4">
          <Badge variant="zinc">Total: {totalCaloriesToday} kcal</Badge>
          <Badge variant="zinc">P: {totalProteinToday}g</Badge>
          <Badge variant="zinc">C: {totalCarbsToday}g</Badge>
          <Badge variant="zinc">F: {totalFatToday}g</Badge>
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Weekly overview</Subheading>
          <Badge variant="zinc">{weeklyAverageCalories} avg kcal</Badge>
        </div>

        <div className="nutrition-weekly-layout mt-5">
          <div className="flex h-56 items-end gap-3">
            {NUTRITION_WEEK.map((day) => {
              const totalHeight = (day.calories / MAX_WEEKLY_CAL) * 100
              const total = day.protein + day.carbs + day.fat
              const proteinPct = (day.protein / total) * totalHeight
              const carbsPct = (day.carbs / total) * totalHeight
              const fatPct = (day.fat / total) * totalHeight

              return (
                <div key={day.day} className="flex flex-1 flex-col items-center gap-2">
                  <span className={`text-xs font-medium ${TEXT_SECONDARY}`}>{day.calories}</span>
                  <div
                    className="flex w-full flex-col overflow-hidden rounded-t-lg"
                    style={{ height: `${totalHeight}%` }}
                  >
                    <div className="w-full dash-viz-tone-1" style={{ flex: `${proteinPct} 0 0` }} />
                    <div className="w-full dash-viz-tone-2" style={{ flex: `${carbsPct} 0 0` }} />
                    <div className="w-full dash-viz-tone-3" style={{ flex: `${fatPct} 0 0` }} />
                  </div>
                  <span className={`text-xs ${TEXT_SUBTLE}`}>{day.day}</span>
                </div>
              )
            })}
          </div>

          <aside className="nutrition-weekly-side space-y-3">
            <SurfaceRow>
              <Eyebrow variant="section" tone="subtle">Highest day</Eyebrow>
              <StrongText className="mt-1">
                {highestCalorieDay.day} · {highestCalorieDay.calories} kcal
              </StrongText>
            </SurfaceRow>
            <SurfaceRow>
              <Eyebrow variant="section" tone="subtle">Lowest day</Eyebrow>
              <StrongText className="mt-1">
                {lowestCalorieDay.day} · {lowestCalorieDay.calories} kcal
              </StrongText>
            </SurfaceRow>
            <SurfaceRow>
              <Eyebrow variant="section" tone="subtle">Macro legend</Eyebrow>
              <div className={`mt-2 space-y-2 text-xs/5 ${TEXT_SECONDARY}`}>
                <p><span className="mr-2 inline-block size-2.5 rounded-full dash-viz-tone-1" />Protein</p>
                <p><span className="mr-2 inline-block size-2.5 rounded-full dash-viz-tone-2" />Carbs</p>
                <p><span className="mr-2 inline-block size-2.5 rounded-full dash-viz-tone-3" />Fat</p>
              </div>
            </SurfaceRow>
          </aside>
        </div>
      </SurfaceCard>
    </div>
  )
}
